import { FundingPolicyType } from '@shared';

// Type for the sections object
type SectionsObject = {
  [key: string]: PolicyFormSection;
};

// Export sections as an object with camelCase keys matching their IDs
export const sharedSections: SectionsObject = {
  /* 1 ▸ Proposed Insured --------------------------------------------------- */
  proposedInsured: {
    id: 'proposed-insured',
    title: 'Proposed Insured',
    subsections: [
      {
        id: 'personal-info',
        title: 'Personal Information',
        questionGroups: [
          {
            id: 'name-sex-dob',
            format: 'grid',
            questions: ['proposedInsuredName', 'proposedInsuredSex', 'proposedInsuredDob'],
            className: 'grid grid-cols-1 md:grid-cols-2 gap-6 items-start',
          },
          {
            id: 'birth-ssn-email',
            format: 'grid',
            questions: [
              'proposedInsuredStateOfBirth',
              'proposedInsuredSsn',
              'proposedInsuredEmail',
            ],
            className: 'grid grid-cols-1 md:grid-cols-2 gap-6 items-start',
          },
          {
            id: 'address-phone',
            format: 'default',
            questions: ['proposedInsuredAddress', 'proposedInsuredPhone'],
          },
          {
            id: 'marital-status',
            format: 'default',
            questions: ['proposedInsuredMaritalStatus', 'proposedInsuredMaritalOther'],
          },
        ],
      },
      {
        id: 'citizenship-info',
        title: 'Citizenship Information',
        questionGroups: [
          {
            id: 'citizenship-status',
            format: 'default',
            questions: ['proposedInsuredCitizenship'],
          },
          {
            id: 'green-card-info',
            format: 'grid',
            questions: [
              'proposedInsuredGreenCardIssueDate',
              'proposedInsuredGreenCardExpirationDate',
            ],
          },
          {
            id: 'visa-info',
            format: 'grid',
            questions: [
              'proposedInsuredVisaType',
              'proposedInsuredVisaIssueDate',
              'proposedInsuredVisaExpirationDate',
            ],
          },
          {
            id: 'other-info',
            format: 'default',
            questions: ['proposedInsuredOther'],
          },
          {
            id: 'us-residence',
            format: 'grid',
            questions: ['proposedInsuredUsResidenceMonths', 'proposedInsuredUsResidenceYears'],
          },
          {
            id: 'language',
            format: 'default',
            questions: ['proposedInsuredUnderstandEnglish', 'proposedInsuredPrimaryLanguage'],
          },
        ],
      },
      {
        id: 'identification-employment',
        title: 'Identification & Employment',
        questionGroups: [
          {
            id: 'identification',
            format: 'grid',
            questions: ['proposedInsuredFormerName', 'proposedInsuredDriversLicense'],
          },
          {
            id: 'employment',
            format: 'grid',
            questions: ['proposedInsuredOccupation', 'proposedInsuredEmployer'],
          },
          {
            id: 'financial',
            format: 'grid',
            questions: ['proposedInsuredAnnualIncome', 'proposedInsuredNetWorth'],
          },
        ],
      },
    ],
  },

  /* 2 ▸ Proposed Additional Insured ---------------------------------------- */
  proposedAdditionalInsured: {
    id: 'proposed-additional-insured',
    title: 'Proposed Additional Insured (CareMatters Together only)',
    conditional: {
      visible: {
        field: 'fundingPolicyType',
        operator: 'equals' as const,
        value: FundingPolicyType.jointCareMatters,
      },
    },
    subsections: [
      {
        id: 'additional-personal-info',
        title: 'Proposed Additional Insured: Personal Information',
        questionGroups: [
          {
            id: 'additional-name-sex-dob',
            format: 'grid',
            questions: ['additionalInsuredName', 'additionalInsuredSex', 'additionalInsuredDob'],
          },
          {
            id: 'additional-birth-ssn-email',
            format: 'grid',
            questions: [
              'additionalInsuredStateOfBirth',
              'additionalInsuredSsn',
              'additionalInsuredEmail',
            ],
          },
          {
            id: 'additional-address',
            format: 'default',
            questions: [
              'additionalInsuredSameAddress',
              'additionalInsuredAddress',
              'additionalInsuredPhone',
            ],
          },
          {
            id: 'additional-marital-status',
            format: 'default',
            questions: ['additionalInsuredMaritalStatus', 'additionalInsuredMarriageOther'],
          },
        ],
      },
      {
        id: 'additional-citizenship-info',
        title: 'Citizenship Information',
        questionGroups: [
          {
            id: 'additional-citizenship-status',
            format: 'default',
            questions: ['additionalInsuredCitizenship'],
          },
          {
            id: 'green-card-info',
            format: 'grid',
            questions: [
              'additionalInsuredGreenCardIssueDate',
              'additionalInsuredGreenCardExpirationDate',
            ],
          },
          {
            id: 'visa-info',
            format: 'grid',
            questions: [
              'additionalInsuredVisaType',
              'additionalInsuredVisaIssueDate',
              'additionalInsuredVisaExpirationDate',
            ],
          },
          {
            id: 'other-info',
            format: 'default',
            questions: ['additionalInsuredOther'],
          },
          {
            id: 'additional-us-residence',
            format: 'grid',
            questions: ['additionalInsuredUsResidenceMonths', 'additionalInsuredUsResidenceYears'],
          },
          {
            id: 'additional-language',
            format: 'default',
            questions: ['additionalInsuredUnderstandEnglish', 'additionalInsuredPrimaryLanguage'],
          },
        ],
      },
      {
        id: 'additional-identification-employment',
        title: 'Identification & Employment',
        questionGroups: [
          {
            id: 'additional-relationship',
            format: 'default',
            questions: ['additionalInsuredRelationship'],
          },
          {
            id: 'additional-identification',
            format: 'grid',
            questions: ['additionalInsuredFormerName', 'additionalInsuredDriversLicense'],
          },
          {
            id: 'additional-employment',
            format: 'grid',
            questions: ['additionalInsuredOccupation', 'additionalInsuredEmployer'],
          },
          {
            id: 'additional-financial',
            format: 'grid',
            questions: ['additionalInsuredAnnualIncome', 'additionalInsuredNetWorth'],
          },
        ],
      },
    ],
  },

  /* 3 ▸ Policy Owner ------------------------------------------------------- */
  policyOwner: {
    id: 'policy-owner',
    title: 'Policy Owner',
    conditional: {
      visible: {
        field: 'policyOwners',
        operator: 'notIn' as const,
        value: ['owner_client', 'owner_joint'],
      },
    },
    subsections: [
      {
        id: 'owner-info',
        title: 'Owner Information',
        conditional: {
          visible: {
            field: 'policyOwners',
            operator: 'notEquals' as const,
            value: 'owner_trust',
          },
        },
        questionGroups: [
          {
            id: 'owner-type',
            format: 'default',
            questions: ['policyOwnerType', 'policyOwnerOtherType'],
          },
          {
            id: 'owner-personal',
            format: 'grid',
            questions: ['policyOwnerName', 'policyOwnerSsn', 'policyOwnerDob'],
          },
          {
            id: 'owner-address',
            format: 'default',
            questions: ['policyOwnerSameAddress', 'policyOwnerAddress', 'policyOwnerPhone'],
          },
          {
            id: 'owner-citizenship',
            format: 'default',
            questions: ['policyOwnerCitizenship'],
          },
          {
            id: 'green-card-info',
            format: 'grid',
            questions: ['policyOwnerGreenCardIssueDate', 'policyOwnerGreenCardExpirationDate'],
          },
          {
            id: 'visa-info',
            format: 'grid',
            questions: [
              'policyOwnerVisaType',
              'policyOwnerVisaIssueDate',
              'policyOwnerVisaExpirationDate',
            ],
          },
          {
            id: 'other-info',
            format: 'default',
            questions: ['policyOwnerOther'],
          },
          {
            id: 'owner-contact',
            format: 'grid',
            questions: ['policyOwnerEmail', 'policyOwnerRelationship'],
          },
          {
            id: 'owner-financial',
            format: 'grid',
            questions: ['policyOwnerAnnualIncome', 'policyOwnerNetWorth'],
          },
        ],
      },
      {
        id: 'trust-info',
        title: 'Trust Information (if applicable)',
        conditional: {
          visible: {
            field: 'policyOwners',
            operator: 'equals' as const,
            value: 'owner_trust',
          },
        },
        questionGroups: [
          {
            id: 'trust-details',
            format: 'grid',
            questions: ['trustName', 'trustTaxId', 'trustDate'],
          },
          {
            id: 'trustees',
            format: 'default',
            questions: ['currentTrustees'],
          },
        ],
      },
    ],
  },

  /* 3.5 ▸ Joint Policy Owner ------------------------------------------------------- */
  jointOwner: {
    id: 'joint-owner',
    title: 'Joint Policy Owner',
    conditional: {
      visible: {
        field: 'policyOwners',
        operator: 'equals' as const,
        value: 'owner_joint_other',
      },
    },
    subsections: [
      {
        id: 'joint-owner-info',
        title: 'Joint Owner Information',
        questionGroups: [
          {
            id: 'joint-owner-type',
            format: 'default',
            questions: ['jointPolicyOwnerType', 'jointPolicyOwnerOtherType'],
          },
          {
            id: 'joint-owner-personal',
            format: 'grid',
            questions: ['jointPolicyOwnerName', 'jointPolicyOwnerSsn', 'jointPolicyOwnerDob'],
          },
          {
            id: 'joint-owner-address',
            format: 'default',
            questions: ['jointPolicyOwnerAddress', 'jointPolicyOwnerPhone'],
          },
          {
            id: 'joint-owner-citizenship',
            format: 'default',
            questions: ['jointPolicyOwnerCitizenship'],
          },
          {
            id: 'green-card-info',
            format: 'grid',
            questions: [
              'jointPolicyOwnerGreenCardIssueDate',
              'jointPolicyOwnerGreenCardExpirationDate',
            ],
          },
          {
            id: 'visa-info',
            format: 'grid',
            questions: [
              'jointPolicyOwnerVisaType',
              'jointPolicyOwnerVisaIssueDate',
              'jointPolicyOwnerVisaExpirationDate',
            ],
          },
          {
            id: 'other-info',
            format: 'default',
            questions: ['jointPolicyOwnerOther'],
          },
          {
            id: 'joint-owner-contact',
            format: 'grid',
            questions: ['jointPolicyOwnerEmail', 'jointPolicyOwnerRelationship'],
          },
        ],
      },
    ],
  },

  /* 4 ▸ Contingent Owner --------------------------------------------------- */
  contingentOwner: {
    id: 'contingent-owner',
    title: 'Contingent Owner',
    subsections: [
      {
        id: 'contingent-owner-info',
        title: 'Contingent Owner Information',
        questionGroups: [
          {
            id: 'contingent-personal',
            format: 'grid',
            questions: ['contingentOwnerName', 'contingentOwnerSsn'],
          },
          {
            id: 'contingent-address',
            format: 'default',
            questions: ['contingentOwnerSameAddress', 'contingentOwnerAddress'],
          },
          {
            id: 'contingent-location',
            format: 'grid',
            questions: [
              'contingentOwnerCity',
              'contingentOwnerState',
              'contingentOwnerZipCode',
              'contingentOwnerCounty',
            ],
          },
          {
            id: 'contingent-relationship',
            format: 'grid',
            questions: ['contingentOwnerRelationship', 'contingentOwnerDob'],
          },
        ],
      },
    ],
  },

  /* 5 ▸ Payor -------------------------------------------------------------- */
  payor: {
    id: 'payor',
    title: 'Payor',
    subsections: [
      {
        id: 'payor-info',
        title: 'Payor Information',
        questionGroups: [
          {
            id: 'payor-personal',
            format: 'grid',
            questions: ['payorName', 'payorSsn', 'payorRelationship'],
          },
          {
            id: 'payor-contact',
            format: 'default',
            questions: ['payorAddress'],
          },
          {
            id: 'payor-communication',
            format: 'grid',
            questions: ['payorEmail', 'payorPhone'],
          },
        ],
      },
    ],
    conditional: {
      visible: {
        field: 'policyPayor',
        operator: 'equals',
        value: 'payor_other',
      },
    },
  },

  /* 6 ▸ Beneficiary Designations ------------------------------------------- */
  beneficiary: {
    id: 'beneficiary',
    title: 'Beneficiary Designations',
    subsections: [
      {
        id: 'beneficiary-info',
        title: 'Beneficiary Information',
        questionGroups: [
          {
            id: 'primary-beneficiary',
            format: 'default',
            questions: ['primaryBeneficiarySameAsOwner'],
          },
          {
            id: 'beneficiary-table',
            format: 'default',
            questions: ['beneficiaryDesignations'],
          },
        ],
      },
    ],
  },

  /* 7 ▸ Premium and Billing Information ------------------------------------ */
  premiumBilling: {
    id: 'premium-billing',
    title: 'Premium and Billing Information',
    subsections: [
      {
        id: 'premium-options',
        title: 'Premium Options',
        questionGroups: [
          {
            id: 'premium-selection',
            format: 'default',
            questions: ['premiumOption'],
          },
          {
            id: 'single-premium',
            format: 'default',
            questions: ['singlePremiumAmount', 'singlePremiumPayment', 'singlePremium1035Exchange'],
          },
          {
            id: 'level-premium',
            format: 'default',
            questions: ['levelPremiumAmount', 'op2PaymentFrequency', 'op2PaymentDuration'],
          },
          {
            id: 'lump-sum-combined',
            format: 'default',
            questions: [
              'lumpSumAmount',
              'lumpSumPayment',
              'lumpSum1035Exchange',
              'combinedLevelPremiumAmount',
            ],
          },
          {
            id: 'op3-payments',
            format: 'grid',
            questions: ['op3PaymentFrequency', 'levelPremiumStartMonth', 'levelPremiumStartYear'],
          },
          {
            id: 'premium-start',
            format: 'default',
            questions: ['op3PaymentDuration'],
          },
        ],
      },
      {
        id: 'payment-methods',
        title: 'Payment Methods',
        questionGroups: [
          {
            id: 'advance-payment',
            format: 'default',
            questions: ['advancePaymentMethod', 'electronicDebitAmount'],
          },
          {
            id: 'future-billing',
            format: 'default',
            questions: [
              'directBill',
              'directBillAmount',
              'listBill',
              'listBillAmount',
              'fundedByAnnuity',
              'fundedByAnnuityAmount',
              'recurringAchDebit',
            ],
          },
        ],
      },
    ],
  },

  /* 8 ▸ CareMatters II Plan Information ------------------------------------ */
  caremattersIIPlan: {
    id: 'carematters-ii-plan',
    title: 'CareMatters® II Plan Information',
    conditional: {
      visible: {
        field: 'fundingPolicyType',
        operator: 'notEquals' as const,
        value: FundingPolicyType.jointCareMatters,
      },
    },
    subsections: [
      {
        id: 'carematters-ii-benefits',
        title: 'Benefits and Options',
        questionGroups: [
          {
            id: 'base-amount',
            format: 'default',
            questions: ['caremattersIIBaseAmount'],
          },
          {
            id: 'benefit-period',
            format: 'default',
            questions: ['ltcSpecifiedBenefitPeriod'],
          },
          {
            id: 'inflation-protection',
            format: 'grid',
            questions: ['ltcInflationProtection'],
          },
          {
            id: 'refund-option',
            format: 'default',
            questions: ['refundOfPremiumOption'],
          },
        ],
      },
    ],
  },

  /* 9 ▸ CareMatters Together Plan Information ------------------------------ */
  caremattersTogetherPlan: {
    id: 'carematters-together-plan',
    title: 'CareMatters Together℠ Plan Information',
    conditional: {
      visible: {
        field: 'fundingPolicyType',
        operator: 'equals' as const,
        value: FundingPolicyType.jointCareMatters,
      },
    },
    subsections: [
      {
        id: 'carematters-together-benefits',
        title: 'Benefits and Options',
        questionGroups: [
          {
            id: 'together-base-amount',
            format: 'default',
            questions: ['caremattersTogetherBaseAmount'],
          },
          {
            id: 'monthly-payments',
            format: 'default',
            questions: ['maxMonthlyLtcPayments'],
          },
          {
            id: 'together-inflation',
            format: 'default',
            questions: ['togetherInflationProtection'],
          },
        ],
      },
    ],
  },

  /* 10 ▸ Protection Against Unintended Lapse ------------------------------- */
  unintendedLapse: {
    id: 'unintended-lapse',
    title: 'Protection Against Unintended Lapse',
    subsections: [
      {
        id: 'third-party-designee',
        title: 'Third Party Designee',
        questionGroups: [
          {
            id: 'designee-election',
            format: 'default',
            questions: ['thirdPartyDesignee'],
          },
          {
            id: 'designee-info',
            format: 'default',
            questions: ['thirdPartyDesigneeName', 'thirdPartyDesigneeAddress'],
          },
        ],
      },
    ],
  },

  /* 11 ▸ Taxpayer Certification -------------------------------------------- */
  taxpayerCertification: {
    id: 'taxpayer-certification',
    title: 'Taxpayer Certification',
    subsections: [
      {
        id: 'backup-withholding',
        title: 'Backup Withholding',
        questionGroups: [
          {
            id: 'withholding-status',
            format: 'default',
            questions: ['backupWithholding'],
          },
        ],
      },
    ],
  },

  /* 12 ▸ Electronic Delivery Election -------------------------------------- */
  electronicDelivery: {
    id: 'electronic-delivery',
    title: 'Electronic Delivery Election',
    subsections: [
      {
        id: 'delivery-preference',
        title: 'Delivery Preference',
        questionGroups: [
          {
            id: 'electronic-option',
            format: 'default',
            questions: ['electronicDelivery'],
          },
        ],
      },
    ],
  },

  /* 13 ▸ Insurance Information --------------------------------------------- */
  insuranceInformation: {
    id: 'insurance-information',
    title: 'Insurance Information',
    subsections: [
      {
        id: 'primary-insured-insurance',
        title: 'Primary Insured Insurance History',
        questionGroups: [
          {
            id: 'primary-current-insurance',
            format: 'default',
            questions: [
              'primaryInsuredOtherInsurance',
              'primaryInsuredSoldPolicy',
              'primaryInsuredRecentApplications',
            ],
          },
          {
            id: 'primary-replacement',
            format: 'default',
            questions: ['primaryInsuredReplacement', 'primaryInsuredPastInsurance'],
          },
          {
            id: 'primary-ltc-coverage',
            format: 'default',
            questions: [
              'primaryInsuredOtherLtc',
              'primaryInsuredTerminatedLtc',
              'primaryInsuredMedicaid',
            ],
          },
          {
            id: 'primary-intentions',
            format: 'default',
            questions: ['primaryInsuredIntendToStop', 'primaryInsuredReplaceMedical'],
          },
        ],
      },
    ],
  },

  /* 13 ▸ Insurance Information Additional Insured --------------------------------------------- */
  insuranceInformationAdditional: {
    id: 'insurance-information-additional-insured',
    title: 'Insurance Information for Additional Insured',
    conditional: {
      visible: {
        field: 'fundingPolicyType',
        operator: 'equals' as const,
        value: FundingPolicyType.jointCareMatters,
      },
    },
    subsections: [
      {
        id: 'additional-insured-insurance',
        title: 'Additional Insured Insurance History',
        questionGroups: [
          {
            id: 'additional-current-insurance',
            format: 'default',
            questions: [
              'additionalInsuredOtherInsurance',
              'additionalInsuredSoldPolicy',
              'additionalInsuredRecentApplications',
            ],
          },
          {
            id: 'additional-replacement',
            format: 'default',
            questions: ['additionalInsuredReplacement', 'additionalInsuredPastInsurance'],
          },
          {
            id: 'additional-ltc-coverage',
            format: 'default',
            questions: [
              'additionalInsuredOtherLtc',
              'additionalInsuredTerminatedLtc',
              'additionalInsuredMedicaid',
            ],
          },
          {
            id: 'additional-intentions',
            format: 'default',
            questions: ['additionalInsuredIntendToStop', 'additionalInsuredReplaceMedical'],
          },
        ],
      },
    ],
  },

  /* 13 ▸ Insurance Information --------------------------------------------- */
  insuranceInformationTable: {
    id: 'insurance-information-table',
    title: 'Insurance Information Table',
    subsections: [
      {
        id: 'insurance-coverage-table',
        title: 'Insurance Coverage Details',
        questionGroups: [
          {
            id: 'coverage-table',
            format: 'default',
            questions: ['insuranceCoverageDetails'],
          },
        ],
      },
    ],
  },

  /* 14 ▸ Financial Information --------------------------------------------- */
  financialInformation: {
    id: 'financial-information',
    title: 'Financial Information',
    subsections: [
      {
        id: 'primary-insured-financial',
        title: 'Financial Information',
        questionGroups: [
          {
            id: 'question-a',
            title:
              'a) Has any party to this application ever sold, assigned, agreed or arranged to sell/assign, or discussed the possibility of selling/assigning this or any other life policy to a life settlement company, trust, limited liability corporation, viatical, or other secondary market purchaser?',
            format: 'grid',
            questions: [
              'primaryInsuredEverSold',
              'additionalInsuredEverSold',
              'policyOwnerEverSold',
            ],
          },
          {
            id: 'question-b',
            title:
              'b) Will any portion of the current or future premium for this policy be financed or will any Policy Owner(s) or Insured(s) receive any payment in connection with the insurance issued on the basis of this application?',
            format: 'grid',
            questions: [
              'primaryInsuredPremiumFinanced',
              'additionalInsuredPremiumFinanced',
              'policyOwnerPremiumFinanced',
            ],
          },
          {
            id: 'financial-table',
            format: 'default',
            questions: ['financialInformationDetails'],
          },
        ],
      },
    ],
  },

  /* 15 ▸ Special Instructions ---------------------------------------------- */
  specialInstructions: {
    id: 'special-instructions',
    title: 'Special Instructions',
    subsections: [
      {
        id: 'instructions',
        title: 'Additional Information',
        questionGroups: [
          {
            id: 'special-notes',
            format: 'default',
            questions: ['specialInstructions'],
          },
        ],
      },
    ],
  },

  /* 16 ▸ Financial Professional Certification ------------------------------ */
  financialProfessional: {
    id: 'financial-professional',
    title: "Financial Professional's Certification",
    subsections: [
      {
        id: 'professional-info',
        title: 'Professional Information',
        questionGroups: [
          {
            id: 'replacement-certification',
            format: 'default',
            questions: ['insuranceWillReplace'],
          },
          {
            id: 'professional-details',
            format: 'grid',
            questions: ['financialProfessionalNameCert', 'firm'],
          },
          {
            id: 'professional-contact',
            format: 'grid',
            questions: [
              'financialProfessionalPhone',
              'nationwideNumber',
              'financialProfessionalEmailCert',
            ],
          },
        ],
      },
    ],
  },

  /* 17 ▸ Receipt of Form Acknowledgment ------------------------------------ */
  formAcknowledgment: {
    id: 'form-acknowledgment',
    title: 'Receipt of Form Acknowledgment',
    subsections: [
      {
        id: 'forms-received',
        title: 'Receipt of Form Acknowledgment',
        questionGroups: [
          {
            id: 'form-checklist',
            format: 'default',
            questions: ['receiptOfForms'],
          },
        ],
      },
    ],
  },

  /* 18 ▸ Temporary Insurance Agreement ------------------------------------- */
  temporaryInsurance: {
    id: 'temporary-insurance',
    title: 'Temporary Insurance Agreement',
    subsections: [
      {
        id: 'temp-insurance-payment',
        title: 'Payment Information',
        questionGroups: [
          {
            id: 'temp-payment',
            format: 'inline',
            questions: ['tempInsurancePayment', 'tempInsuranceBaseAmount'],
          },
        ],
      },
      {
        id: 'health-questions',
        title: 'Primary Insured Health Questions',
        questionGroups: [
          {
            id: 'primary-health',
            format: 'default',
            questions: ['primaryInsuredHealthQ1', 'primaryInsuredHealthQ2'],
          },
        ],
      },
      {
        id: 'health-questions-additional-insured',
        title: 'Additional Insured Health Questions',
        conditional: {
          visible: {
            field: 'fundingPolicyType',
            operator: 'equals' as const,
            value: FundingPolicyType.jointCareMatters,
          },
        },
        questionGroups: [
          {
            id: 'additional-health',
            format: 'default',
            questions: ['additionalInsuredHealthQ1', 'additionalInsuredHealthQ2'],
          },
        ],
      },
    ],
  },
};
